import React, { useEffect, useState, useMemo } from 'react';
import { Table, Pagination } from '@gerdau/hefesto-mais-web';
import { Button, Icon, Tooltip } from '@gerdau/hefesto';
import {
  IDestination,
  ILongSteelOrderItem,
  IMeasureUnit,
  IPaymentTerm
} from '../../../../../store/ducks/long-steel-shopping-cart/types';
import {
  Container,
  ButtonContainer,
  PlusCircleIcon,
  MultiSelectDescription
} from './styles';
import MeasureUnitSelect from './MeasureUnitSelect';
import QuantityInput from '../../../../../components/Purchase/QuantityInput';
import PaymentWaySelect from './PaymentWaySelect';
import DateInput from '../../../../../components/Purchase/DateInput';
import { useHistory, useLocation } from 'react-router-dom';
import { ERoutes } from '../../../../../routes';
import { EOrderSettingsRoutes } from '../orderSettings.routes';
import { EPurchaseRoutes } from '../../../purchase.routes';
import moment from 'moment';
import CustomerPurchaseItemInput from './CustomerPurchaseItemInput';
import DestinationSelect from './DestinationSelect';
import FiscalBenefitSelect from './FiscalBenefitSelect';
import { useDispatch, useSelector } from 'react-redux';
import {
  areMeasuresConvertible,
  convertInputedValue,
  isInvalidMultipleValue,
  isLoweThanMinimumQty,
  isOutOfMinimunDate,
  isSelectedAnInvalidMeasureUnit,
  isWeekend
} from './shoppingCartFieldsValidation';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { missingNumbers } from '../../../../../utils/missingNumbers';
import { ApplicationState } from '../../../../../store';
import { ICustomer } from '../../../../../store/ducks/customers/types';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import { EMeasureUnit } from '../../../../../store/commomTypes';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';

interface ShoppingCartTableDataSource {
  key: number;
  materialDescription: ILongSteelOrderItem;
  unity: ILongSteelOrderItem;
  quantity: ILongSteelOrderItem;
  customerPurchaseItem: ILongSteelOrderItem;
  date: ILongSteelOrderItem;
  destination: ILongSteelOrderItem;
  paymentWay: ILongSteelOrderItem;
  benefit: ILongSteelOrderItem;
  removeItem: number;
  isMultiSelectItem?: boolean;
}

interface ShoppingCartTableProps {
  items: ILongSteelOrderItem[];
  emitterId: string;
  onUpdateCartItem(): void;
  onRemoveItem(idxsToRemove: number[]): void;
  numberMaxOfItens: number;
}

enum EShoppingCartErrorTypes {
  QUANTITY = 'QUANTITY',
  MINIMUN_DATE = 'MINIMUN_DATE',
  WEEKEND_DATE = 'WEEKEND_DATE',
  MEASURE_UNIT = 'MEASURE_UNIT'
}

enum EEarlyPaymentTypes {
  EARLY_PAYMENT_BY_BANK_SLIP = 'B010',
  EARLY_PAYMENT_BY_BANK_DEPOSIT = 'B014',
  EARLY_PAYMENT_BY_CASH = 'Z000'
}

const errorMessagesMap = new Map<EShoppingCartErrorTypes, React.ReactNode>();
errorMessagesMap.set(
  EShoppingCartErrorTypes.QUANTITY,
  <>
    Alguns produtos estão com <b>quantidades</b> fora dos mínimos ou múltiplos.
  </>
);
errorMessagesMap.set(
  EShoppingCartErrorTypes.MINIMUN_DATE,
  <>
    Alguns produtos estão com <b>datas desejadas</b> anteriores às datas mais
    próximas disponíveis.
  </>
);
errorMessagesMap.set(
  EShoppingCartErrorTypes.WEEKEND_DATE,
  <>
    Alguns produtos estão com <b>datas desejadas</b> no sábado ou domingo.
  </>
);
errorMessagesMap.set(
  EShoppingCartErrorTypes.MEASURE_UNIT,
  <>
    Alguns produtos estão com <b>unid de medidas</b> inválidas.
  </>
);

const ShoppingCartTable: React.FC<ShoppingCartTableProps> = ({
  items,
  emitterId,
  onUpdateCartItem,
  onRemoveItem,
  numberMaxOfItens
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { pathname } = useLocation();
  const { commerceScreenName } = getTaggingInformationScreen(pathname);

  const customers = useSelector((state: ApplicationState) => state.customers);

  const [saleItems, setSaleItems] = useState(items);
  const [dataTable, setDataTable] = useState<ShoppingCartTableDataSource[]>([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(11);

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const [multiSelectedMeasureUnit, setMultiSelectedMeasureUnit] =
    useState<IMeasureUnit>();
  const [multiSelectedDate, setMultiSelectedDate] = useState<string>('');
  const [multiSelectedQuantity, setMultiSelectedQuantity] =
    useState<string>('');

  const [
    multiSelectedDestinationDescription,
    setMultiSelectedDestinationDescription
  ] = useState<string>('');
  const [multiplePaymentTermsOptions, setMultiplePaymentTermsOptions] =
    useState<IPaymentTerm[]>([]);
  const [multiSelectedTerm, setMultiSelectedTerm] = useState<string>('');

  const [alreadyInitialized, setAlreadyInitialized] = useState(false);

  const [isUserHasBlock, setIsUserIsBlock] = useState(false);

  const earlyPaymentTerms = useMemo(
    () => Object.values(EEarlyPaymentTypes),
    []
  );

  const taggingInfoPreviousScreen = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const checkCustomerHasBlock = () => {
    const customer = customers.customers.find(
      (customer) => customer.customerId === emitterId
    ) as ICustomer;

    if (customer?.blocks?.length) {
      setIsUserIsBlock(true);
    }
  };

  const getPaymentsOptions = (
    selectedDestination?: IDestination
  ): IPaymentTerm[] => {
    if (!selectedDestination) {
      return [];
    }
    if (isUserHasBlock) {
      return selectedDestination.paymentTerms.filter((payment) =>
        earlyPaymentTerms.some((el) => payment.term.includes(el))
      );
    }
    return selectedDestination.paymentTerms;
  };

  const startErrorsMessageValidation = () => {
    setAlreadyInitialized(true);

    const shopCartErrors: EShoppingCartErrorTypes[] = [];
    const createAlertMessage = (
      message: React.ReactNode,
      durationInMS: number
    ) => {
      dispatch(
        sendTagging({
          event: 'error',
          screenName: commerceScreenName,
          mensagem: message
        })
      );
      addToast({ type: EToastTypes.DANGER, message, durationInMS });
    };

    items.forEach((item) => {
      const selectedMeasureUnit = getSelectedMeasureUnit(item);
      const quantityValue = item.itemSaleInfo?.quantity
        ? item.itemSaleInfo.quantity
        : '';

      // MeasureUnit
      if (
        !shopCartErrors.includes(EShoppingCartErrorTypes.MEASURE_UNIT) &&
        isSelectedAnInvalidMeasureUnit(selectedMeasureUnit, item.measureUnit)
      ) {
        shopCartErrors.push(EShoppingCartErrorTypes.MEASURE_UNIT);
        createAlertMessage(
          errorMessagesMap.get(EShoppingCartErrorTypes.MEASURE_UNIT),
          8000
        );
      }

      // QuantityInput
      if (
        !shopCartErrors.includes(EShoppingCartErrorTypes.QUANTITY) &&
        selectedMeasureUnit !== undefined &&
        (isInvalidMultipleValue(selectedMeasureUnit, quantityValue) ||
          isLoweThanMinimumQty(selectedMeasureUnit, quantityValue))
      ) {
        shopCartErrors.push(EShoppingCartErrorTypes.QUANTITY);
        createAlertMessage(
          errorMessagesMap.get(EShoppingCartErrorTypes.QUANTITY),
          8500
        );
      }

      // Date
      if (
        !shopCartErrors.includes(EShoppingCartErrorTypes.MINIMUN_DATE) &&
        isOutOfMinimunDate(
          item.destinations[0].minimumDate,
          item.itemSaleInfo?.requiredDate ? item.itemSaleInfo.requiredDate : ''
        )
      ) {
        shopCartErrors.push(EShoppingCartErrorTypes.MINIMUN_DATE);
        createAlertMessage(
          errorMessagesMap.get(EShoppingCartErrorTypes.MINIMUN_DATE),
          9000
        );
      }

      if (
        !shopCartErrors.includes(EShoppingCartErrorTypes.WEEKEND_DATE) &&
        item.itemSaleInfo?.requiredDate &&
        isWeekend(moment(item.itemSaleInfo.requiredDate, 'YYYY-MM-DD'))
      ) {
        shopCartErrors.push(EShoppingCartErrorTypes.WEEKEND_DATE);
        createAlertMessage(
          errorMessagesMap.get(EShoppingCartErrorTypes.WEEKEND_DATE),
          10500
        );
      }
    });
  };
  useEffect(() => {
    if (!alreadyInitialized) {
      startErrorsMessageValidation();
    }
  });

  const getSelectedMeasureUnit = (
    item: ILongSteelOrderItem
  ): IMeasureUnit | undefined => {
    return item.itemSaleInfo?.unityMeasure
      ? item.destinations[0].measureUnits.find(
          (unit) => unit.measure === item.itemSaleInfo.unityMeasure
        )
      : undefined;
  };

  const getChangedSaleItem = (
    saleItem: ILongSteelOrderItem
  ): ILongSteelOrderItem => {
    return saleItems.find(
      (item) => item.itemWeb === saleItem.itemWeb && item.sku === saleItem.sku
    ) as ILongSteelOrderItem;
  };

  const getMultiplePaymentTermsOptions = (
    destinationDescription: string
  ): IPaymentTerm[] => {
    const allDestinations: IDestination[] = [];
    items
      .filter((_, idx) => selectedRowKeys.includes(idx))
      .forEach((item) => {
        const selectedDestination = item.destinations.find(
          (dest) => dest.description === destinationDescription
        ) as IDestination;
        allDestinations.push(selectedDestination);
      });

    const allPaymentTerms: IPaymentTerm[] = [];

    allDestinations.forEach((dest) => {
      getPaymentsOptions(dest).forEach((pt) => {
        if (!allPaymentTerms.find((allPT) => allPT.term === pt.term)) {
          allPaymentTerms.push(pt);
        }
      });
    });

    return allPaymentTerms;
  };

  const setUnitMeasure = (
    saleItem: ILongSteelOrderItem,
    unit: IMeasureUnit
  ) => {
    const itemChanged = getChangedSaleItem(saleItem);
    let convertedQuantity = '';
    const convertibleMeasures = [EMeasureUnit.KG, EMeasureUnit.T];
    const isConvertible = areMeasuresConvertible(
      convertibleMeasures,
      itemChanged.itemSaleInfo.unityMeasure as EMeasureUnit,
      unit.measure
    );

    if (itemChanged.itemSaleInfo?.quantity && isConvertible) {
      convertedQuantity = convertInputedValue(
        itemChanged.itemSaleInfo.quantity,
        unit,
        itemChanged.itemSaleInfo.unityMeasure
      );
    }
    itemChanged.itemSaleInfo = {
      ...itemChanged.itemSaleInfo,
      unityMeasure: unit.measure,
      multipleQuantity: unit.multiple,
      minimumQuantity: unit.minimumQuantity
    };
    if (convertedQuantity.length) {
      itemChanged.itemSaleInfo = {
        ...itemChanged.itemSaleInfo,
        quantity: convertedQuantity
      };
    }

    setSaleItems([...saleItems]);
    onUpdateCartItem();
  };

  const setMultiUnitMeasure = (unit: IMeasureUnit) => {
    setMultiSelectedMeasureUnit(unit);
    setMultiSelectedQuantity('');
    selectedRowKeys.forEach((i) => {
      const saleItemUnit = saleItems[i].destinations[0].measureUnits.find(
        (munit) => munit.measure === unit.measure
      );
      if (saleItemUnit) {
        setUnitMeasure(saleItems[i], saleItemUnit);
      }
    });
  };

  const setQuantity = (saleItem: ILongSteelOrderItem, value: string) => {
    const itemChanged = getChangedSaleItem(saleItem);
    itemChanged.itemSaleInfo.quantity = value;
    setSaleItems([...saleItems]);
    onUpdateCartItem();
  };

  const setMultiQuatity = (value: string) => {
    setMultiSelectedQuantity(Number(value).toString());
    selectedRowKeys.forEach((i) => {
      if (
        saleItems[i].itemSaleInfo &&
        saleItems[i].itemSaleInfo.unityMeasure ===
          multiSelectedMeasureUnit?.measure
      ) {
        let newValue =
          Number(value) * Number(saleItems[i].itemSaleInfo.multipleQuantity);

        if (newValue < Number(saleItems[i].itemSaleInfo.minimumQuantity || '0'))
          newValue = 0;

        if (
          newValue <= Number.MAX_SAFE_INTEGER &&
          !Number.isInteger(newValue)
        ) {
          setQuantity(saleItems[i], newValue.toFixed(3));
          return;
        }
        setQuantity(saleItems[i], newValue.toString());
      }
    });
  };

  const setCustomerPurchaseItem = (
    saleItem: ILongSteelOrderItem,
    value: string
  ) => {
    const itemChanged = getChangedSaleItem(saleItem);
    itemChanged.itemSaleInfo.customerPurchaseItem = value;
    setSaleItems([...saleItems]);
    onUpdateCartItem();
  };

  const setDate = (saleItem: ILongSteelOrderItem, date: string) => {
    const itemChanged = getChangedSaleItem(saleItem);
    itemChanged.itemSaleInfo.requiredDate = date;
    setSaleItems([...saleItems]);
    onUpdateCartItem();
  };

  const setMultiDate = (date: string) => {
    setMultiSelectedDate(date);
    selectedRowKeys.forEach((i) => {
      if (
        moment(date, 'YYYY-MM-DD').isSameOrAfter(
          moment(saleItems[i].destinations[0].minimumDate, 'YYYY-MM-DD')
        )
      ) {
        setDate(saleItems[i], date);
      }
    });
  };

  const setDestination = (
    saleItem: ILongSteelOrderItem,
    destinationDescription: string
  ) => {
    const itemChanged = getChangedSaleItem(saleItem);
    const selectedDestination = saleItem.destinations.find(
      (dest) => dest.description === destinationDescription
    ) ?? { ...saleItem.destinations[0], description: destinationDescription };
    itemChanged.itemSaleInfo = {
      ...itemChanged.itemSaleInfo,
      selectedDestination,
      term: '',
      benefit: ''
    };

    setSaleItems([...saleItems]);
    onUpdateCartItem();
  };

  const setMultiDestination = (destinationDescription: string) => {
    setMultiSelectedDestinationDescription(destinationDescription);
    setMultiplePaymentTermsOptions(
      getMultiplePaymentTermsOptions(destinationDescription)
    );
    setMultiSelectedTerm('');
    selectedRowKeys.forEach((i) => {
      if (
        saleItems[i].destinations.find(
          (dest) => dest.description === destinationDescription
        )
      ) {
        setDestination(saleItems[i], destinationDescription);
      }
    });
  };

  const setTerm = (saleItem: ILongSteelOrderItem, term: string) => {
    const itemChanged = getChangedSaleItem(saleItem);
    itemChanged.itemSaleInfo.term = term;
    setSaleItems([...saleItems]);
    onUpdateCartItem();
  };

  const setMultiTerm = (term: string) => {
    setMultiSelectedTerm(term);
    selectedRowKeys.forEach((i) => {
      const selectedDestination = saleItems[i].destinations.find(
        (dest) => dest.description === multiSelectedDestinationDescription
      ) as IDestination;
      if (selectedDestination.paymentTerms.find((pt) => pt.term === term)) {
        setTerm(saleItems[i], term);
      } else {
        setTerm(saleItems[i], '');
      }
    });
  };

  const setBenefit = (saleItem: ILongSteelOrderItem, benefit: string) => {
    const itemChanged = getChangedSaleItem(saleItem);
    itemChanged.itemSaleInfo.benefit = benefit;
    setSaleItems([...saleItems]);
    onUpdateCartItem();
  };

  const onMultiRemove = () => {
    onRemoveItem(selectedRowKeys);
    setSelectedRowKeys([]);
  };

  const getColumns = () => {
    return [
      {
        title: 'Item Nº',
        dataIndex: 'customerPurchaseItem',
        width: 100,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => {
          if (record.isMultiSelectItem) {
            setTimeout(() => {
              const bounceEl = document.querySelector('.bounce-right');
              if (bounceEl) bounceEl.classList.remove('bounce-right');
            }, 2500);
          }

          return (
            <>
              {record.isMultiSelectItem ? (
                <MultiSelectDescription className="bounce-right">
                  Use para aplicar aos <strong>itens selecionados</strong>
                  <div className="right-arrow"></div>
                </MultiSelectDescription>
              ) : (
                <CustomerPurchaseItemInput
                  customerPurchaseItem={
                    item.itemSaleInfo?.customerPurchaseItem ?? ''
                  }
                  setCustomerPurchaseItem={(value) =>
                    setCustomerPurchaseItem(item, value)
                  }
                />
              )}
            </>
          );
        }
      },
      {
        title: 'Descrição do Material',
        dataIndex: 'materialDescription',
        width: 250,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => {
          return <>{record.isMultiSelectItem ? '' : item.description}</>;
        }
      },
      {
        title: 'Unidade',
        dataIndex: 'unity',
        width: 150,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => (
          <>
            {record.isMultiSelectItem ? (
              <MeasureUnitSelect
                measureUnits={item.destinations[0].measureUnits}
                selected={multiSelectedMeasureUnit}
                onSelectUnit={setMultiUnitMeasure}
                isMultipleSelectField={true}
              />
            ) : (
              <MeasureUnitSelect
                measureUnits={item.destinations[0].measureUnits}
                selected={getSelectedMeasureUnit(item)}
                onSelectUnit={(unit: IMeasureUnit) =>
                  setUnitMeasure(item, unit)
                }
                itemMeasureUnit={item.measureUnit}
              />
            )}
          </>
        )
      },
      {
        title: 'Quantidade',
        dataIndex: 'quantity',
        width: 20,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => (
          <>
            {record.isMultiSelectItem ? (
              <Tooltip
                placement="bottom"
                title="Este valor será múltiplo dos demais números"
              >
                <div>
                  <QuantityInput
                    value={multiSelectedQuantity}
                    measureUnit={multiSelectedMeasureUnit}
                    onChange={setMultiQuatity}
                    isMultipleSelectField
                  />
                </div>
              </Tooltip>
            ) : (
              <QuantityInput
                value={
                  item.itemSaleInfo?.quantity ? item.itemSaleInfo.quantity : ''
                }
                measureUnit={getSelectedMeasureUnit(item)}
                onChange={(value: string) => setQuantity(item, value)}
              />
            )}
          </>
        )
      },
      {
        title: 'Data desejada',
        dataIndex: 'date',
        width: 200,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => (
          <>
            {record.isMultiSelectItem ? (
              <DateInput
                value={multiSelectedDate}
                minimumDate={item.destinations[0].minimumDate}
                onSelectDate={setMultiDate}
              />
            ) : (
              <DateInput
                value={
                  item.itemSaleInfo?.requiredDate
                    ? item.itemSaleInfo.requiredDate
                    : ''
                }
                minimumDate={item.destinations[0].minimumDate}
                onSelectDate={(date: string) => setDate(item, date)}
              />
            )}
          </>
        )
      },
      {
        title: 'Destinação',
        dataIndex: 'destination',
        width: 200,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => (
          <>
            {record.isMultiSelectItem ? (
              <DestinationSelect
                selectedDestination={multiSelectedDestinationDescription}
                onSelectDestination={setMultiDestination}
                destinations={item.destinations}
              />
            ) : (
              <DestinationSelect
                selectedDestination={
                  item.itemSaleInfo?.selectedDestination
                    ? item.itemSaleInfo.selectedDestination.description
                    : ''
                }
                onSelectDestination={(destinationDescription: string) =>
                  setDestination(item, destinationDescription)
                }
                destinations={item.destinations}
              />
            )}
          </>
        )
      },
      {
        title: 'Forma de pagamento',
        dataIndex: 'paymentWay',
        width: 200,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => (
          <>
            {record.isMultiSelectItem ? (
              <PaymentWaySelect
                paymentTerms={multiplePaymentTermsOptions}
                selectedTerm={multiSelectedTerm}
                onSelectTerm={setMultiTerm}
                disabled={!multiSelectedDestinationDescription}
              />
            ) : (
              <PaymentWaySelect
                paymentTerms={getPaymentsOptions(
                  item.itemSaleInfo.selectedDestination
                )}
                selectedTerm={
                  item.itemSaleInfo?.term ? item.itemSaleInfo.term : ''
                }
                onSelectTerm={(term: string) => setTerm(item, term)}
                disabled={!item.itemSaleInfo?.selectedDestination}
              />
            )}
          </>
        )
      },
      {
        title: 'Benefício fiscal',
        dataIndex: 'benefit',
        width: 200,
        render: (
          item: ILongSteelOrderItem,
          record: ShoppingCartTableDataSource
        ) => (
          <>
            {record.isMultiSelectItem && <> - </>}

            {!record.isMultiSelectItem && (
              <>
                {(!item.itemSaleInfo?.selectedDestination ||
                  (item.itemSaleInfo?.selectedDestination.fiscalBenefits !==
                    undefined &&
                    item.itemSaleInfo?.selectedDestination.fiscalBenefits
                      .length === 0)) && (
                  <span className="span-facelift">Não utilizar benefício</span>
                )}

                {item.itemSaleInfo?.selectedDestination &&
                  item.itemSaleInfo?.selectedDestination.fiscalBenefits !==
                    undefined &&
                  item.itemSaleInfo?.selectedDestination.fiscalBenefits.length >
                    0 && (
                    <FiscalBenefitSelect
                      fiscalBenefits={
                        item.itemSaleInfo.selectedDestination.fiscalBenefits
                      }
                      selectedFiscalBenefit={item.itemSaleInfo.benefit ?? ''}
                      onSelectFiscalBenefit={(benefit: string) =>
                        setBenefit(item, benefit)
                      }
                    />
                  )}
              </>
            )}
          </>
        )
      },

      {
        title: '',
        dataIndex: 'removeItem',
        width: 20,
        render: (idx: number, record: ShoppingCartTableDataSource) => (
          <>
            {record.isMultiSelectItem && (
              <Button
                name="trashButton"
                variant="tertiary"
                icon={<Icon option="Trash" />}
                onClick={onMultiRemove}
              />
            )}

            {!record.isMultiSelectItem && !selectedRowKeys.includes(idx) && (
              <Button
                name="trashButton"
                variant="tertiary"
                icon={<Icon option="Trash" />}
                onClick={() => {
                  onRemoveItem([idx]);
                }}
              />
            )}
          </>
        )
      }
    ];
  };

  const updateItemsDataTable = (
    orderItems: ILongSteelOrderItem[],
    page: number,
    pageSize: number
  ) => {
    const dataTableItems = orderItems.map((item, idx) => ({
      key: page !== 1 ? idx + (page - 1) * pageSize : idx,
      materialDescription: item,
      unity: item,
      quantity: item,
      customerPurchaseItem: item,
      date: item,
      destination: item,
      paymentWay: item,
      benefit: item,
      removeItem: page !== 1 ? idx + (page - 1) * pageSize : idx
    }));

    setDataTable(dataTableItems);
  };

  const getMultipleSelectRow = (): ShoppingCartTableDataSource => {
    const multiSelectUnits: IMeasureUnit[] = [];
    let minimumDate = '';
    const destinations: IDestination[] = [];

    items.forEach((item) => {
      item.destinations[0].measureUnits.forEach((munit) => {
        if (!multiSelectUnits.find((u) => u.measure === munit.measure)) {
          multiSelectUnits.push({ ...munit, multiple: '1' });
        }
      });

      if (
        !minimumDate ||
        moment(item.destinations[0].minimumDate, 'YYYY-MM-DD').isBefore(
          moment(minimumDate, 'YYYY-MM-DD')
        )
      ) {
        minimumDate = item.destinations[0].minimumDate;
      }

      item.destinations.forEach((itemDest) => {
        if (
          !destinations.find(
            (dest) => dest.description === itemDest.description
          )
        ) {
          destinations.push(itemDest);
        }
      });
    });

    const multiSelectItem: ILongSteelOrderItem = {
      ...items[0],
      destinations: destinations.map((dest) => {
        return { ...dest, measureUnits: multiSelectUnits, minimumDate };
      }),
      itemSaleInfo: {}
    };

    return {
      key: -1,
      materialDescription: multiSelectItem,
      unity: multiSelectItem,
      quantity: multiSelectItem,
      customerPurchaseItem: multiSelectItem,
      date: multiSelectItem,
      destination: multiSelectItem,
      paymentWay: multiSelectItem,
      benefit: multiSelectItem,
      removeItem: -1,
      isMultiSelectItem: true
    };
  };

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: `LG:Comprar:AcosLongos:${taggingInfoPreviousScreen.screenName}:Detalhamento`,
        event: 'page_view'
      })
    );

    checkCustomerHasBlock();
  }, []);

  useEffect(() => {
    if (items.length > 0 && (items !== saleItems || dataTable.length < 1)) {
      setSaleItems(items);
      handleChangePagination(currentPage, pageSize);
    } else if (items.length === 0) {
      // Se entrar no carrinho sem itens, é redirecionado para 'purchase/'
      history.push(`${ERoutes.PURCHASE}`);
    }
  }, [items]);

  const handleChangePagination = (page: number, newPageTotal: number) => {
    const sliceItems = items.slice(
      (page - 1) * newPageTotal,
      page * newPageTotal
    );
    setCurrentPage(page);
    setPageSize(newPageTotal);
    updateItemsDataTable(sliceItems, page, newPageTotal);
  };

  const onAddProductClick = () => {
    history.push(
      `${ERoutes.PURCHASE}/${EPurchaseRoutes.LONG_STEEL}/${EOrderSettingsRoutes.SEARCH_ITEMS}`
    );
  };

  const rowSelection = {
    onChange: (
      _selectedRowKeys: unknown[],
      _: unknown,
      { type }: { type: string }
    ) => {
      if (
        type === 'single' &&
        selectedRowKeys.length > _selectedRowKeys.length
      ) {
        const missingItemsKeys = missingNumbers(
          _selectedRowKeys as number[],
          (currentPage - 1) * pageSize,
          currentPage * pageSize
        );

        const filterSelectedRowsKeys = Array.from(
          new Set(selectedRowKeys.concat(_selectedRowKeys as number[]))
        ).filter((key: number) => !missingItemsKeys.includes(key));

        setSelectedRowKeys(filterSelectedRowsKeys);
        return;
      }
      if (type === 'all' && _selectedRowKeys.length) {
        const selectedRowsKeys = items.map(
          (_items: ILongSteelOrderItem, idx: number) => idx
        );
        setSelectedRowKeys(selectedRowsKeys);
        return;
      }
      setSelectedRowKeys(_selectedRowKeys as number[]);
    },
    getCheckboxProps: (record: ShoppingCartTableDataSource) => ({
      disabled: record.key === -1
    })
  };

  return (
    <Container>
      {items.length > 0 && (
        <>
          <Table
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys,
              columnWidth: 35,
              ...rowSelection
            }}
            columns={getColumns()}
            dataSource={
              selectedRowKeys.length > 0
                ? [getMultipleSelectRow(), ...dataTable]
                : dataTable
            }
            pagination={false}
          />
          <Pagination
            total={items.length}
            onChange={handleChangePagination}
            showTotal={true}
          />
        </>
      )}
      <ButtonContainer>
        <Button
          variant="primary"
          name="addProductButton"
          icon={<PlusCircleIcon />}
          onClick={() => onAddProductClick()}
          disabled={items.length >= numberMaxOfItens}
        >
          Adicionar produto
        </Button>
      </ButtonContainer>
    </Container>
  );
};

export default ShoppingCartTable;
